
import React, { useState, useEffect } from 'react';
import { HashRouter as BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Auth } from './pages/Auth';
import { Home } from './pages/Home';
import { Collection } from './pages/Collection';
import { PlantForm } from './pages/PlantForm';
import { RequireSubscription } from './components/RequireSubscription';
import { Plans } from './pages/Plans';
import { PlantDetails } from './pages/PlantDetails';
import { PlantReport } from './pages/PlantReport';
import { Profile } from './pages/Profile';
import { Info } from './pages/Info';
import { PlantAnalysis } from './pages/PlantAnalysis';
import { Layout } from './components/Layout';
import { supabase } from './services/supabase';
import { storage } from './services/storage';
import { notifications } from './services/notification';
import { APP_INFO } from './constants';
import { Sparkles, KeyRound, AlertCircle } from 'lucide-react';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isRecovering, setIsRecovering] = useState(false);
  const [hasApiKey, setHasApiKey] = useState<boolean | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  // Verificação para validar se as variáveis do objeto de configuração estão acessíveis 
  // globalmente, prevenindo falhas de referência na renderização dos assets.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('ServiceWorker registration successful with scope: ', registration.scope);
        })
        .catch((err) => {
          console.log('ServiceWorker registration failed: ', err);
        });
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__APP_CONFIG__ = APP_INFO;
      
      if (!APP_INFO || typeof APP_INFO.LOGO_URL === 'undefined') {
        console.error("⚠️ Configuração de assets falhou: O objeto APP_INFO não carregou corretamente as referências da imagem da logo.");
      }
    }
  }, []);

  useEffect(() => {
    const handleQuotaError = (e: any) => {
      if (e.reason?.name === 'QuotaExceededError' || e.reason?.message?.includes('quota')) {
        alert("O armazenamento do seu navegador está cheio. Por favor, vá em Perfil > Limpar Cache para liberar espaço.");
      }
    };
    window.addEventListener('unhandledrejection', handleQuotaError);
    return () => window.removeEventListener('unhandledrejection', handleQuotaError);
  }, []);

  useEffect(() => {
    const checkApiKey = async () => {
      if (typeof window !== 'undefined' && (window as any).aistudio) {
        const hasKey = await (window as any).aistudio.hasSelectedApiKey();
        setHasApiKey(hasKey);
      } else {
        // Fallback for environments without aistudio global
        setHasApiKey(true);
      }
    };
    checkApiKey();
  }, []);

  const handleOpenKeyDialog = async () => {
    if ((window as any).aistudio) {
      await (window as any).aistudio.openSelectKey();
      setHasApiKey(true); // Assume success as per guidelines
    }
  };

  useEffect(() => {
    const checkInitialSession = async () => {
      const timeout = setTimeout(() => {
        if (isAuthenticated === null) {
          setIsAuthenticated(false);
        }
      }, 3000);

      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        clearTimeout(timeout);
        
        if (session?.user && !error) {
          setIsAuthenticated(true);
          
          if (window.location.search.includes('mock=true')) {
            const urlParams = new URLSearchParams(window.location.search);
            const planType = urlParams.get('plan') || 'monthly';
            
            supabase.from('subscriptions').upsert({
              user_id: session.user.id,
              stripe_customer_id: 'mock_cus_' + Date.now(),
              stripe_subscription_id: 'mock_sub_' + Date.now(),
              status: 'active',
              plan_type: planType,
              current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              updated_at: new Date().toISOString()
            }, { onConflict: 'user_id' }).then(() => {
              window.history.replaceState({}, document.title, window.location.pathname);
              window.location.reload();
            });
          }
          storage.ensureUserExists(session.user).catch(console.error);
          notifications.checkAndNotifyReminders();
        } else {
          setIsAuthenticated(false);
        }
      } catch (err: any) {
        clearTimeout(timeout);
        if (err.message?.toLowerCase().includes("supabase")) {
          setConfigError(err.message);
        }
        setIsAuthenticated(false);
      }
    };

    checkInitialSession();

    let subscription: any = null;
    try {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          setIsRecovering(true);
          setIsAuthenticated(false);
        } else if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          if (!isRecovering) {
            setIsAuthenticated(true);
            if (session?.user) {
              storage.ensureUserExists(session.user).catch(console.error);
              notifications.checkAndNotifyReminders();
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setIsAuthenticated(false);
          setIsRecovering(false);
        }
      });
      subscription = data.subscription;
    } catch (err: any) {
      if (err.message?.toLowerCase().includes("supabase")) {
        setConfigError(err.message);
      }
    }

    return () => subscription?.unsubscribe();
  }, [isRecovering]);

  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        notifications.checkAndNotifyReminders();
      }, 1000 * 60 * 60); 
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await storage.logout();
    } finally {
      setIsAuthenticated(false);
    }
  };

  if (configError) {
    return (
      <div className="min-h-screen bg-raizama-bg flex flex-col items-center justify-center p-8 text-center animate-fade-in">
        <div className="bg-raizama-surface border border-red-500/30 p-10 rounded-[3rem] shadow-2xl max-w-md w-full backdrop-blur-md">
          <div className="w-20 h-20 bg-red-500/10 rounded-3xl flex items-center justify-center text-red-500 mx-auto mb-8 shadow-inner">
            <AlertCircle size={40} />
          </div>
          <h2 className="text-2xl font-black text-white mb-4 tracking-tight">Erro de Configuração</h2>
          <p className="text-raizama-textSec text-sm leading-relaxed mb-8">
            {configError}
          </p>
          <div className="p-4 bg-black/20 rounded-2xl border border-white/5 text-left mb-8">
            <p className="text-[10px] text-raizama-textSec uppercase font-black tracking-widest mb-2">Como resolver:</p>
            <ol className="text-[10px] text-raizama-textSec space-y-2 list-decimal pl-4">
              <li>Acesse as configurações do seu projeto no Supabase.</li>
              <li>Copie a <b>Project URL</b> e a <b>Anon Key</b>.</li>
              <li>Adicione as variáveis <code>VITE_SUPABASE_URL</code> e <code>VITE_SUPABASE_ANON_KEY</code> no ambiente do AI Studio.</li>
            </ol>
          </div>
          <p className="text-[9px] text-raizama-textSec/50 uppercase font-bold">O aplicativo será reiniciado automaticamente após a configuração.</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated === null || hasApiKey === null) {
    return (
      <div className="min-h-screen bg-raizama-bg flex flex-col items-center justify-center p-10 text-center">
        <img src={APP_INFO?.LOGO_URL || ''} alt="Raizama Logo" className="w-20 h-20 mb-6 animate-pulse" />
        <div className="w-6 h-6 border-2 border-raizama-action border-t-transparent rounded-full animate-spin"></div>
        <p className="text-raizama-textMain font-black mt-4 uppercase tracking-[0.3em] text-[9px] opacity-40">
          Sincronizando Jardim...
        </p>
      </div>
    );
  }

  if (!hasApiKey) {
    return (
      <div className="min-h-screen bg-raizama-bg flex flex-col items-center justify-center p-8 text-center animate-fade-in">
        <div className="bg-raizama-surface border border-raizama-border/20 p-10 rounded-[3rem] shadow-2xl max-w-md w-full backdrop-blur-md">
          <div className="w-20 h-20 bg-raizama-action/10 rounded-3xl flex items-center justify-center text-raizama-action mx-auto mb-8 shadow-inner">
            <Sparkles size={40} />
          </div>
          <h2 className="text-2xl font-black text-white mb-4 tracking-tight">Ativar Inteligência Botânica</h2>
          <p className="text-raizama-textSec text-sm leading-relaxed mb-8">
            Para utilizar os recursos avançados de identificação e diagnóstico por imagem, é necessário configurar sua chave de API do Google Cloud (Gemini).
          </p>
          
          <div className="space-y-4">
            <button 
              onClick={handleOpenKeyDialog}
              className="w-full bg-raizama-action text-white font-black py-4 rounded-2xl shadow-xl shadow-raizama-action/20 flex items-center justify-center gap-3 transition-all hover:brightness-110 active:scale-95 uppercase tracking-widest text-xs"
            >
              <KeyRound size={18} />
              Selecionar Chave API
            </button>
            
            <a 
              href="https://ai.google.dev/gemini-api/docs/billing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block text-[10px] text-raizama-textSec hover:text-raizama-action transition-colors uppercase font-bold tracking-widest"
            >
              Saiba mais sobre faturamento
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || isRecovering) {
    return <Auth 
      onLogin={() => {
        setIsAuthenticated(true);
        setIsRecovering(false);
      }} 
      initialMode={isRecovering ? 'recovery' : 'login'} 
    />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout onLogout={handleLogout}><Home /></Layout>} />
        <Route path="/collection" element={<Layout title="Minha Coleção" showBack onLogout={handleLogout}><Collection /></Layout>} />
        <Route path="/analysis" element={<Layout title="Botânico IA" showBack onLogout={handleLogout}><PlantAnalysis /></Layout>} />
        <Route path="/plant/:id" element={<Layout title="Detalhes" showBack onLogout={handleLogout}><PlantDetails /></Layout>} />
        <Route path="/plant/:id/report" element={<Layout title="Relatório" showBack onLogout={handleLogout}><RequireSubscription><PlantReport /></RequireSubscription></Layout>} />
        <Route path="/add" element={<Layout title="Nova Planta" showBack onLogout={handleLogout}><PlantForm /></Layout>} />
        <Route path="/edit/:id" element={<Layout title="Editar Planta" showBack onLogout={handleLogout}><PlantForm /></Layout>} />
        <Route path="/profile" element={<Layout title="Perfil" showBack onLogout={handleLogout}><Profile /></Layout>} />
        <Route path="/plans" element={<Layout title="Assinatura" showBack onLogout={handleLogout}><Plans /></Layout>} />
        <Route path="/info" element={<Layout title="Sobre" showBack onLogout={handleLogout}><Info /></Layout>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
